/**
 * The timeline row deriver (ticket 3525, upstream pin e3e7cc3f, MIT), ported
 * from upstream `MessagesTimeline.logic.ts` onto forge's session-logic
 * vocabulary. Turn folds, shared live activity rows, tool-group toggles,
 * subagent spawn rows, question/answer rows, and thinking traces replace
 * forge's inline burst grouping and completion divider.
 *
 * Forge deltas: entries carrying a brokered GitHub blocker (confirmation or
 * action required) always render as their own row instead of joining a group,
 * so the approval cards stay reachable. Synthetic worktree admission is also
 * a dedicated row so it stays in chronological order with the current turn.
 *
 * @module MessagesTimeline.logic
 */

import type { MessageId, TurnId } from "@cmmd-forge/contracts";
import {
  inferCheckpointTurnCountByTurnId,
  workLogEntryIsToolLike,
  type TimelineEntry,
  type WorkLogEntry,
} from "../../session-logic";
import type { TurnDiffSummary } from "../../types";
import {
  omitSupersededLifecycleMarkers,
  summarizeToolGroup,
  toolGroupAction,
} from "../../workLogToolGroups";
import {
  singleToolCallLabel,
  resolveAssistantMessageCopyState,
  shouldPreserveAssistantLineBreaks,
  resolveWorkGroupScrollIndex,
  shouldFollowWorkGroupAppend,
} from "./MessagesTimeline.labels";
import { deriveActiveWorkState } from "./MessagesTimeline.liveActivity";
import {
  attachTrailingToolGroupsToAssistant,
  buildWorkGroupToggleRow,
  deriveTerminalAssistantMessageIds,
  expandedWorkGroupRow,
  isActivityEntry,
  workEntryIsVisibleInGroup,
  workGroupId,
  workGroupIdentity,
} from "./MessagesTimeline.activityGroups";
import {
  deriveActiveVisualResponseTurnIds,
  deriveTurnFolds,
  deriveUnsettledTurnId,
  lastUserMessageIndex,
  timelineEntryTurnId,
} from "./MessagesTimeline.turnFolds";
import {
  computeMessageDurationStart,
  type MessagesTimelineRow,
  type TimelineLatestTurn,
  LIVE_ACTIVITY_ROW_ID,
} from "./MessagesTimeline.rowTypes";
import {
  buildRevertTurnCountByUserMessageId,
  projectMessagesTimelineRows as deriveMessagesTimelineRowsWithStateImpl,
  type MessagesTimelineRowsProjection,
} from "./MessagesTimeline.projection";

export {
  computeMessageDurationStart,
  computeStableMessagesTimelineRows,
} from "./MessagesTimeline.rowTypes";
export type { MessagesTimelineRow, TimelineLatestTurn } from "./MessagesTimeline.rowTypes";
export type { MessagesTimelineRowsProjection } from "./MessagesTimeline.projection";
export { liveWorkEntryLabel, workEntryDisplayLabel } from "./MessagesTimeline.labels";
export {
  resolveAssistantMessageCopyState,
  shouldPreserveAssistantLineBreaks,
  resolveWorkGroupScrollIndex,
  shouldFollowWorkGroupAppend,
};

export { normalizeCompactToolLabel } from "../../workLogToolPresentation";

// fallow-ignore-next-line complexity -- port of upstream MessagesTimeline.logic.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
export function deriveMessagesTimelineRows(input: {
  timelineEntries: ReadonlyArray<TimelineEntry>;
  latestTurn?: TimelineLatestTurn | null;
  runningTurnId?: TurnId | null;
  expandedTurnIds?: ReadonlySet<TurnId>;
  expandedWorkGroupIds?: ReadonlySet<string>;
  isWorking: boolean;
  activeTurnStartedAt: string | null;
  turnDiffSummaries: ReadonlyArray<TurnDiffSummary>;
  supportsConversationRollback: boolean;
  /** Task ids of subagents still working, used by the active tool indicator. */
  liveAgentTaskIds?: ReadonlySet<string> | undefined;
}): MessagesTimelineRow[] {
  const turnDiffSummaryByAssistantMessageId = new Map<MessageId, TurnDiffSummary>();
  for (const summary of input.turnDiffSummaries) {
    if (summary.assistantMessageId) {
      turnDiffSummaryByAssistantMessageId.set(summary.assistantMessageId, summary);
    }
  }
  const revertTurnCountByUserMessageId = buildRevertTurnCountByUserMessageId({
    supportsConversationRollback: input.supportsConversationRollback,
    timelineEntries: input.timelineEntries,
    turnDiffSummaryByAssistantMessageId,
    inferredCheckpointTurnCountByTurnId: input.supportsConversationRollback
      ? inferCheckpointTurnCountByTurnId([...input.turnDiffSummaries])
      : {},
  });
  const nextRows: MessagesTimelineRow[] = [];
  const durationStartByMessageId = computeMessageDurationStart(
    input.timelineEntries.flatMap((entry) => (entry.kind === "message" ? [entry.message] : [])),
  );
  const terminalAssistantMessageIds = deriveTerminalAssistantMessageIds(input.timelineEntries);
  const unsettledTurnId = deriveUnsettledTurnId(
    input.latestTurn ?? null,
    input.runningTurnId ?? null,
  );
  const activeVisualResponseTurnIds = deriveActiveVisualResponseTurnIds({
    timelineEntries: input.timelineEntries,
    unsettledTurnId,
    isWorking: input.isWorking,
  });
  const foldsByAnchorEntryId = deriveTurnFolds({
    timelineEntries: input.timelineEntries,
    terminalAssistantMessageIds,
    latestTurn: input.latestTurn ?? null,
    unfoldedTurnIds: activeVisualResponseTurnIds,
  });
  const collapsedEntryIds = new Set<string>();
  for (const fold of foldsByAnchorEntryId.values()) {
    if (!input.expandedTurnIds?.has(fold.turnId)) {
      for (const entryId of fold.hiddenEntryIds) {
        collapsedEntryIds.add(entryId);
      }
    }
  }

  const latestUserMessageIndex = lastUserMessageIndex(input.timelineEntries);
  const {
    activeTurnHeaderIndex,
    activeWorkRow,
    activeWorkEntryIds,
    latestToolFailed,
    latestToolKeepsActivityLive,
    hasLatestVisibleToolEntry,
    activeWorkAnchorId: activeWorkPlacementEntryId,
  } = deriveActiveWorkState({
    timelineEntries: input.timelineEntries,
    isWorking: input.isWorking,
    unsettledTurnId,
    expandedWorkGroupIds: input.expandedWorkGroupIds,
    liveAgentTaskIds: input.liveAgentTaskIds,
    lastUserMessageIndex: latestUserMessageIndex,
  });
  const workEntryIsInActiveRun = (entry: WorkLogEntry) =>
    input.isWorking &&
    unsettledTurnId !== null &&
    entry.toolLifecycleStatus === "inProgress" &&
    entry.turnId === unsettledTurnId;
  const appendWorkingRow = () => {
    const latestUserMessage = input.timelineEntries[latestUserMessageIndex];
    const visualResponseStartedAt =
      activeVisualResponseTurnIds.size > 1 &&
      latestUserMessage?.kind === "message" &&
      latestUserMessage.message.role === "user"
        ? latestUserMessage.message.createdAt
        : input.activeTurnStartedAt;
    nextRows.push({
      kind: "working",
      id: "working-indicator-row",
      createdAt: visualResponseStartedAt,
    });
  };
  let hasActivityRow = false;
  const appendActiveWorkRows = () => {
    if (activeWorkRow === null) return;
    nextRows.push(activeWorkRow);
    hasActivityRow ||= activeWorkRow.active;
    if (!activeWorkRow.expanded || activeWorkRow.entry.agentSpawn) return;
    nextRows.push(
      expandedWorkGroupRow(
        activeWorkRow.groupId,
        activeWorkRow.createdAt,
        activeWorkRow.groupedEntries,
      ),
    );
  };

  let scannedActivityThrough = -1;
  for (let index = 0; index < input.timelineEntries.length; index += 1) {
    const timelineEntry = input.timelineEntries[index];
    if (!timelineEntry) {
      continue;
    }

    if (input.isWorking && index === activeTurnHeaderIndex) {
      appendWorkingRow();
    }

    if (timelineEntry.id === activeWorkPlacementEntryId) {
      appendActiveWorkRows();
    }

    const anchoredTurnFold = foldsByAnchorEntryId.get(timelineEntry.id);
    if (anchoredTurnFold) {
      nextRows.push({
        kind: "turn-fold",
        id: `turn-fold:${anchoredTurnFold.turnId}`,
        createdAt: anchoredTurnFold.createdAt,
        turnId: anchoredTurnFold.turnId,
        label: anchoredTurnFold.label,
        expanded: input.expandedTurnIds?.has(anchoredTurnFold.turnId) ?? false,
      });
    }

    if (collapsedEntryIds.has(timelineEntry.id)) {
      continue;
    }

    const activityTurnId = timelineEntryTurnId(timelineEntry);
    if (index > scannedActivityThrough && activityTurnId && isActivityEntry(timelineEntry)) {
      const entries = [timelineEntry];
      let cursor = index + 1;
      while (cursor < input.timelineEntries.length) {
        const next = input.timelineEntries[cursor]!;
        if (
          !isActivityEntry(next) ||
          timelineEntryTurnId(next) !== activityTurnId ||
          collapsedEntryIds.has(next.id) ||
          foldsByAnchorEntryId.has(next.id)
        )
          break;
        entries.push(next);
        cursor += 1;
      }
      scannedActivityThrough = cursor - 1;
      if (entries.some((entry) => entry.kind === "message")) {
        const active =
          input.isWorking &&
          activityTurnId === unsettledTurnId &&
          cursor === input.timelineEntries.length &&
          !latestToolFailed &&
          (!hasLatestVisibleToolEntry || latestToolKeepsActivityLive);
        const groupId =
          timelineEntry.kind === "work"
            ? workGroupId(timelineEntry.id, timelineEntry.entry)
            : `activity-group:${timelineEntry.id}`;
        nextRows.push({
          kind: "activity-group",
          id: active ? LIVE_ACTIVITY_ROW_ID : groupId,
          createdAt: timelineEntry.createdAt,
          turnId: activityTurnId,
          groupId,
          entries,
          expanded: input.expandedWorkGroupIds?.has(groupId) ?? false,
          active,
        });
        hasActivityRow ||= active;
        index = cursor - 1;
        continue;
      }
    }

    if (activeWorkEntryIds.has(timelineEntry.id)) {
      continue;
    }

    if (
      timelineEntry.kind === "work" &&
      timelineEntry.entry.sourceActivityKind === "context-compaction"
    ) {
      nextRows.push({
        kind: "context-compaction",
        id: timelineEntry.id,
        createdAt: timelineEntry.createdAt,
        label: timelineEntry.entry.label,
      });
      continue;
    }

    if (timelineEntry.kind === "work") {
      if (
        timelineEntry.entry.agentSpawn !== undefined ||
        timelineEntry.entry.questionAnswer !== undefined ||
        timelineEntry.entry.tone === "error" ||
        timelineEntry.entry.githubConfirmation !== undefined ||
        timelineEntry.entry.githubActionRequired !== undefined
      ) {
        const spawn = timelineEntry.entry.agentSpawn;
        if (
          spawn &&
          input.isWorking &&
          index >= activeTurnHeaderIndex &&
          (unsettledTurnId === null || timelineEntryTurnId(timelineEntry) === unsettledTurnId)
        ) {
          hasActivityRow ||=
            (spawn.workflowId !== null && input.liveAgentTaskIds?.has(spawn.workflowId)) ||
            spawn.agentTaskIds.some((taskId) => input.liveAgentTaskIds?.has(taskId));
        }
        nextRows.push({
          kind: "work",
          id: timelineEntry.id,
          createdAt: timelineEntry.createdAt,
          groupedEntries: [timelineEntry.entry],
          isExpandedToolGroup: false,
        });
        continue;
      }
      const groupedEntries = [timelineEntry.entry];
      let cursor = index + 1;
      while (cursor < input.timelineEntries.length) {
        const nextEntry = input.timelineEntries[cursor];
        if (
          !nextEntry ||
          nextEntry.kind !== "work" ||
          nextEntry.entry.agentSpawn !== undefined ||
          nextEntry.entry.questionAnswer !== undefined ||
          nextEntry.entry.sourceActivityKind === "context-compaction" ||
          nextEntry.entry.tone === "error" ||
          nextEntry.entry.githubConfirmation !== undefined ||
          nextEntry.entry.githubActionRequired !== undefined ||
          activeWorkEntryIds.has(nextEntry.id) ||
          collapsedEntryIds.has(nextEntry.id) ||
          foldsByAnchorEntryId.has(nextEntry.id)
        ) {
          break;
        }
        groupedEntries.push(nextEntry.entry);
        cursor += 1;
      }
      const visibleGroupedEntries = omitSupersededLifecycleMarkers(
        groupedEntries.filter((entry) =>
          workEntryIsVisibleInGroup(entry, workEntryIsInActiveRun(entry)),
        ),
        (entry) => entry,
      );
      if (visibleGroupedEntries.length > 0) {
        const activeInProgressToolEntries = visibleGroupedEntries.filter(workEntryIsInActiveRun);
        if (activeInProgressToolEntries.length > 0) {
          const groupId = workGroupId(timelineEntry.id, timelineEntry.entry);
          const expanded = input.expandedWorkGroupIds?.has(groupId) ?? false;
          const latestActiveToolEntry = activeInProgressToolEntries.at(-1)!;
          nextRows.push({
            kind: "work-live",
            id: `work-live:${workGroupIdentity(timelineEntry.id, timelineEntry.entry)}`,
            createdAt: timelineEntry.createdAt,
            entry: latestActiveToolEntry,
            groupedEntries: visibleGroupedEntries,
            groupId,
            expanded,
            active: true,
          });
          hasActivityRow = true;
          if (expanded) {
            nextRows.push(
              expandedWorkGroupRow(groupId, timelineEntry.createdAt, visibleGroupedEntries),
            );
          }
        } else if (
          visibleGroupedEntries.length === 1 &&
          workLogEntryIsToolLike(visibleGroupedEntries[0]!)
        ) {
          const singleEntry = visibleGroupedEntries[0]!;
          nextRows.push({
            kind: "work",
            id: timelineEntry.id,
            createdAt: timelineEntry.createdAt,
            groupedEntries: visibleGroupedEntries,
            isExpandedToolGroup: false,
            displayLabel:
              toolGroupAction(singleEntry) === "edit"
                ? summarizeToolGroup(visibleGroupedEntries)
                : singleToolCallLabel(singleEntry),
          });
        } else {
          const groupId = workGroupId(timelineEntry.id, timelineEntry.entry);
          const expanded = input.expandedWorkGroupIds?.has(groupId) ?? false;
          nextRows.push(
            buildWorkGroupToggleRow({
              visibleGroupedEntries,
              groupId,
              timelineEntryId: timelineEntry.id,
              createdAt: timelineEntry.createdAt,
              turnId: timelineEntry.entry.turnId ?? null,
              expanded,
            }),
          );
          if (expanded) {
            nextRows.push(
              expandedWorkGroupRow(groupId, timelineEntry.createdAt, visibleGroupedEntries),
            );
          }
        }
      }
      index = cursor - 1;
      continue;
    }

    if (timelineEntry.kind === "proposed-plan") {
      nextRows.push({
        kind: "proposed-plan",
        id: timelineEntry.id,
        createdAt: timelineEntry.createdAt,
        proposedPlan: timelineEntry.proposedPlan,
      });
      continue;
    }

    const assistantResponseStillInProgress =
      timelineEntry.message.role === "assistant" &&
      timelineEntry.message.turnId !== null &&
      timelineEntry.message.turnId !== undefined &&
      activeVisualResponseTurnIds.has(timelineEntry.message.turnId);

    const durationStart =
      durationStartByMessageId.get(timelineEntry.message.id) ?? timelineEntry.message.createdAt;

    // While the turn is still running, the latest assistant message is only
    // provisionally terminal — withhold the metadata row until the turn
    // settles so commentary doesn't flash timestamps mid-work.
    const showAssistantMeta =
      timelineEntry.message.role === "assistant" &&
      terminalAssistantMessageIds.has(timelineEntry.message.id) &&
      !assistantResponseStillInProgress;

    nextRows.push({
      kind: "message",
      id: timelineEntry.id,
      createdAt: timelineEntry.createdAt,
      message: timelineEntry.message,
      durationStart,
      showAssistantMeta,
      showAssistantCopyButton: showAssistantMeta,
      assistantCopyStreaming: timelineEntry.message.streaming || assistantResponseStillInProgress,
      assistantTurnDiffSummary:
        timelineEntry.message.role === "assistant"
          ? turnDiffSummaryByAssistantMessageId.get(timelineEntry.message.id)
          : undefined,
      revertTurnCount:
        timelineEntry.message.role === "user"
          ? revertTurnCountByUserMessageId.get(timelineEntry.message.id)
          : undefined,
    });
  }

  const hasWorkingRow = nextRows.some((row) => row.kind === "working");
  if (input.isWorking && !hasWorkingRow && activeTurnHeaderIndex === input.timelineEntries.length) {
    appendWorkingRow();
  }
  if (input.isWorking && (!hasActivityRow || latestToolFailed)) {
    nextRows.push({
      kind: "thinking",
      id: LIVE_ACTIVITY_ROW_ID,
      createdAt: input.activeTurnStartedAt,
    });
  }
  const rows = attachTrailingToolGroupsToAssistant(nextRows);
  return rows;
}

/** Public two-argument shape, matching upstream's signature at the pin. */
export function deriveMessagesTimelineRowsWithState(
  input: Parameters<typeof deriveMessagesTimelineRows>[0],
  previous: MessagesTimelineRowsProjection | null = null,
): MessagesTimelineRowsProjection {
  return deriveMessagesTimelineRowsWithStateImpl(input, previous, deriveMessagesTimelineRows);
}
